import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAppContext } from '../../context/AppContext'; // Import useAppContext
import apiService from '../../services/api';

// Reusable component for a save button that saves an item to the user's saved items
const SaveButton = ({ tmsId, connectorId, programType, buttonClassName = '', showText = false }) => {
  const { t } = useAppContext(); // Get translation function
  const [savedEpisode, setSavedEpisode] = useState(false);
  const [savedParent, setSavedParent] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user } = useAuth();
  
  const userId = user?.user_id;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if the items are already saved when the component mounts or IDs change
  useEffect(() => {
    const checkSavedStatuses = async () => {
      setIsLoading(true);
      let episodeSaved = false;
      let parentSaved = false;

      if (userId) {
        if (tmsId) {
          try {
            const result = await apiService.checkSavedItem(userId, tmsId);
            episodeSaved = result.saved;
          } catch (err) {
            console.error('Failed to check if episode is saved: ', err);
          }
        }

        if (connectorId && programType === 'SERIES EPISODE') {
          try {
            const result = await apiService.checkSavedItem(userId, connectorId);
            parentSaved = result.saved;
          } catch (err) {
            console.error('Failed to check if parent is saved: ', err);
          }
        }
      }
      setSavedEpisode(episodeSaved);
      setSavedParent(parentSaved);
      setIsLoading(false);
    };

    checkSavedStatuses();
  }, [tmsId, connectorId, programType, userId]);

  const handleSaveToggle = useCallback(async (idToSave, isCurrentlySaved, setSavedState) => {
    if (!idToSave || !userId) return;

    setIsAnimating(true);

    try {
      if (isCurrentlySaved) {
        await apiService.deleteSavedItem(userId, idToSave);
        setSavedState(false);
      } else {
        await apiService.saveItem(userId, idToSave);
        setSavedState(true);
      }
    } catch (err) {
      console.error(`Failed to toggle saved status for ${idToSave}: `, err);
    } finally {
      setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
  }, [userId]);

  const handleEpisodeSaveClick = useCallback((e) => {
    if (e) { // Only stop propagation if event object exists
      e.stopPropagation();
    }
    handleSaveToggle(tmsId, savedEpisode, setSavedEpisode);
    setIsDropdownOpen(false);
  }, [tmsId, savedEpisode, handleSaveToggle]);

  const handleParentSaveClick = useCallback((e) => {
    e.stopPropagation();
    handleSaveToggle(connectorId, savedParent, setSavedParent);
    setIsDropdownOpen(false);
  }, [connectorId, savedParent, handleSaveToggle]);

  const buttonClasses = `p-0.5 rounded hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all duration-300 relative transform ${
    isAnimating ? 'animate-bounce scale-110' : 'hover:scale-105'
  } ${buttonClassName}`;

  if (!tmsId || !userId) return null;

  const isSeriesEpisode = programType === 'SERIES EPISODE';
  const showDropdown = isSeriesEpisode && connectorId;

  const mainButtonText = isLoading ? t('loading') : (savedEpisode ? t('saveButton.saved') : t('saveButton.save')); // Use t() for text
  const mainButtonTitle = savedEpisode ? t('saveButton.remove') : t('saveButton.add'); // Assuming these keys will be added

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => showDropdown ? setIsDropdownOpen(!isDropdownOpen) : handleEpisodeSaveClick(e)}
        className={buttonClasses}
        title={mainButtonTitle}
        aria-label={mainButtonTitle}
        disabled={isLoading}
      >
        {showText ? (
          <span className={`text-sm ${savedEpisode ? 'text-red-600' : 'text-gray-500'} flex items-center`}>
            <span className="mr-1">
              {savedEpisode ? (
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M5 3a2 2 0 00-2 2v12l7-3 7 3V5a2 2 0 00-2-2H5z" />
                </svg>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3-7 3V5z" />
                </svg>
              )}
            </span>
            {mainButtonText}
            {showDropdown && (
              <svg className={`ml-1 h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            )}
          </span>
        ) : (
          <span className={`text-sm ${savedEpisode ? 'text-red-600' : 'text-gray-500'}`}>
            {savedEpisode ? (
              <svg className="h-4 w-4 inline-block align-middle" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M5 3a2 2 0 00-2 2v12l7-3 7 3V5a2 2 0 00-2-2H5z" />
              </svg>
            ) : (
              <svg className="h-4 w-4 inline-block align-middle" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3-7 3V5z" />
              </svg>
            )}
          </span>
        )}
      </button>

      {showDropdown && isDropdownOpen && (
        <div className="absolute z-50 mt-1 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
            <button
              onClick={handleEpisodeSaveClick}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              role="menuitem"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : (savedEpisode ? `Unsave Episode ID ${tmsId}` : `Save Episode ID ${tmsId}`)}
            </button>
            <button
              onClick={handleParentSaveClick}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              role="menuitem"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : (savedParent ? `Unsave Parent ID ${connectorId}` : `Save Parent ID ${connectorId}`)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaveButton;
